import React, { useCallback, useState } from 'react';
import { Activity, Save, RefreshCw, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import AdvancedOptionsSection from './AdvancedOptionsSection';
import GoalAndFormulaSection from './GoalAndFormulaSection';
import DietPreferencesSection from './DietPreferencesSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateAge } from '../../../../../utils/calculations/date';
import { calculateCalories } from '../../../../../utils/calculations/health';

/**
 * Компонент основной формы расчета TDEE (Total Daily Energy Expenditure)
 * Позволяет пользователю ввести антропометрические данные и рассчитать
 * свою суточную потребность в калориях.
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  generateNutritionPlan,
  activityLevels,
  goals,
  formulas,
  saveProfile 
}) => {
  
  // Состояние для имени сохраняемого профиля
  const [saveProfileName, setSaveProfileName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeBodyFat' && !checked) {
        newState.bodyFat = '';
      }
      
      if (name === 'includeAdvancedOptions' && !checked) {
        newState.dailySteps = '7500';
        newState.workoutDays = '3';
        newState.workoutIntensity = 'moderate';
        newState.stress = 'low';
        newState.sleepHours = '7';
      }

      // Смена единиц измерения
      if (name === 'measurementSystem') {
        if (value === 'imperial' && prev.measurementSystem === 'metric') {
          // Конвертация из метрической в имперскую
          const weightLbs = Math.round(parseFloatOrDefault(prev.weight) * 2.20462);
          const heightInches = Math.round(parseFloatOrDefault(prev.height) * 0.393701);
          newState.weight = weightLbs.toString();
          newState.height = heightInches.toString();
        } else if (value === 'metric' && prev.measurementSystem === 'imperial') {
          // Конвертация из имперской в метрическую
          const weightKg = Math.round(parseFloatOrDefault(prev.weight) * 0.453592);
          const heightCm = Math.round(parseFloatOrDefault(prev.height) * 2.54);
          newState.weight = weightKg.toString();
          newState.height = heightCm.toString();
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);
  
  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      gender, age, weight, height, activityLevel, goal, formula,
      includeBodyFat, bodyFat, includeAdvancedOptions, dailySteps,
      workoutDays, sleepHours
    } = formData;

    // Парсинг числовых значений
    const numAge = parseFloatOrDefault(age, -1);
    const numWeight = parseFloatOrDefault(weight, -1);
    const numHeight = parseFloatOrDefault(height, -1);
    
    // Валидация базовых полей
    if (numAge <= 0) newErrors.age = 'Возраст должен быть положительным числом.';
    if (!newErrors.age && numAge > 120) newErrors.age = 'Максимальный возраст 120 лет.';
    
    if (numWeight <= 0) newErrors.weight = 'Вес должен быть положительным числом.';
    if (formData.measurementSystem === 'metric') {
      if (!newErrors.weight && numWeight > 300) newErrors.weight = 'Максимальный вес 300 кг.';
    } else {
      if (!newErrors.weight && numWeight > 660) newErrors.weight = 'Максимальный вес 660 фунтов.';
    }
    
    if (numHeight <= 0) newErrors.height = 'Рост должен быть положительным числом.';
    if (formData.measurementSystem === 'metric') {
      if (!newErrors.height && numHeight > 250) newErrors.height = 'Максимальный рост 250 см.';
    } else {
      if (!newErrors.height && numHeight > 96) newErrors.height = 'Максимальный рост 96 дюймов (8 футов).';
    }

    // Валидация процента жира в организме, если включена эта опция
    if (includeBodyFat) {
      const numBodyFat = parseFloatOrDefault(bodyFat, -1);
      if (numBodyFat <= 0) newErrors.bodyFat = 'Процент жира должен быть положительным числом.';
      if (!newErrors.bodyFat && numBodyFat > 70) newErrors.bodyFat = 'Максимальный % жира - 70%.';
    }

    // Валидация дополнительных параметров, если они включены
    if (includeAdvancedOptions) {
      const numDailySteps = parseFloatOrDefault(dailySteps, -1);
      if (numDailySteps < 0) newErrors.dailySteps = 'Количество шагов не может быть отрицательным.';
      if (!newErrors.dailySteps && numDailySteps > 50000) newErrors.dailySteps = 'Максимум 50,000 шагов.';
      
      const numWorkoutDays = parseFloatOrDefault(workoutDays, -1);
      if (numWorkoutDays < 0) newErrors.workoutDays = 'Значение не может быть отрицательным.';
      if (!newErrors.workoutDays && numWorkoutDays > 7) newErrors.workoutDays = 'Максимум 7 дней в неделю.';
      
      const numSleepHours = parseFloatOrDefault(sleepHours, -1);
      if (numSleepHours < 0) newErrors.sleepHours = 'Значение не может быть отрицательным.';
      if (!newErrors.sleepHours && numSleepHours > 24) newErrors.sleepHours = 'Максимум 24 часа в сутки.';
    }

    // Убедитесь, что выбран уровень активности, цель и формула
    if (!activityLevel) newErrors.activityLevel = 'Выберите уровень активности.';
    if (!goal) newErrors.goal = 'Выберите цель.';
    if (!formula) newErrors.formula = 'Выберите формулу расчета.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);
  
  // Расчет TDEE (Total Daily Energy Expenditure)
  const calculateTDEE = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        let { 
          gender, age, weight, height, activityLevel, goal, formula,
          measurementSystem, includeBodyFat, bodyFat, includeAdvancedOptions,
          dailySteps, workoutDays, workoutIntensity, stress, sleepHours, adjustForAge
        } = formData;

        // Конвертация из имперских в метрические единицы для расчетов
        let weightInKg = parseFloatOrDefault(weight);
        let heightInCm = parseFloatOrDefault(height);
        
        if (measurementSystem === 'imperial') {
          weightInKg = weightInKg * 0.453592; // фунты в кг
          heightInCm = heightInCm * 2.54; // дюймы в см
        }
        
        // Парсинг других числовых значений
        const ageValue = parseFloatOrDefault(age);
        const bodyFatValue = includeBodyFat ? parseFloatOrDefault(bodyFat) : null;
        const dailyStepsValue = includeAdvancedOptions ? parseFloatOrDefault(dailySteps) : 7500;
        const workoutDaysValue = includeAdvancedOptions ? parseFloatOrDefault(workoutDays) : 3;
        const sleepHoursValue = includeAdvancedOptions ? parseFloatOrDefault(sleepHours) : 7;

        // Находим активность из списка по id
        const activityFactor = activityLevels.find(a => a.id === activityLevel)?.factor || 1.2;
        
        // Находим цель из списка по id
        const goalFactor = goals.find(g => g.id === goal)?.factor || 1.0;
        
        // --- Расчет базового метаболизма (BMR) ---
        let bmr = 0;
        
        if (formula === 'mifflin' || (formula === 'katch' && !bodyFatValue)) {
          // Формула Миффлина-Сан Жеора (более современная и точная для большинства людей)
          if (gender === 'male') {
            bmr = 10 * weightInKg + 6.25 * heightInCm - 5 * ageValue + 5;
          } else {
            bmr = 10 * weightInKg + 6.25 * heightInCm - 5 * ageValue - 161;
          }
        } else if (formula === 'harris') {
          // Формула Харриса-Бенедикта (классическая, немного завышающая)
          if (gender === 'male') {
            bmr = 88.362 + (13.397 * weightInKg) + (4.799 * heightInCm) - (5.677 * ageValue);
          } else {
            bmr = 447.593 + (9.247 * weightInKg) + (3.098 * heightInCm) - (4.330 * ageValue);
          }
        } else if (formula === 'katch' && bodyFatValue) {
          // Формула Катч-МакАрдла (использует процент жира)
          const leanBodyMass = weightInKg * (1 - (bodyFatValue / 100));
          bmr = 370 + (21.6 * leanBodyMass);
        }
        
        // --- Расчет TDEE с учетом уровня активности ---
        let tdee = Math.round(bmr * activityFactor);
        
        // --- Применение дополнительных коррекций, если включены ---
        if (includeAdvancedOptions) {
          // Коррекция для шагов (если отличается от среднего 7500)
          if (dailyStepsValue !== 7500) {
            const stepsDifference = dailyStepsValue - 7500;
            const caloriesPerStep = 0.04; // примерно 0.04 ккал на шаг
            tdee += Math.round(stepsDifference * caloriesPerStep);
          }
          
          // Коррекция для тренировок
          let workoutIntensityFactor = 0;
          switch (workoutIntensity) {
            case 'light': workoutIntensityFactor = 100; break;
            case 'moderate': workoutIntensityFactor = 200; break;
            case 'intense': workoutIntensityFactor = 300; break;
            case 'very_intense': workoutIntensityFactor = 450; break;
            default: workoutIntensityFactor = 0;
          }
          tdee += Math.round((workoutDaysValue * workoutIntensityFactor) / 7); // равномерно на всю неделю
          
          // Коррекция для стресса
          let stressFactor = 0;
          switch (stress) {
            case 'high': stressFactor = 0.05; break; // увеличение на 5%
            case 'moderate': stressFactor = 0.025; break; // увеличение на 2.5%
            case 'low': stressFactor = 0; break;
            default: stressFactor = 0;
          }
          tdee += Math.round(tdee * stressFactor);
          
          // Коррекция для сна (если отличается от обычных 7-8 часов)
          if (sleepHoursValue < 7) {
            // Недостаток сна может увеличить TDEE из-за гормональных изменений
            tdee += Math.round(tdee * 0.05); // примерно +5% к TDEE
          }
        }
        
        // Корректировка для пожилых людей (снижение метаболизма)
        if (adjustForAge && ageValue > 60) {
          const ageFactor = Math.min(0.15, (ageValue - 60) * 0.005); // до 15% снижения после 60 лет
          tdee -= Math.round(tdee * ageFactor);
        }
        
        // Корректировка для подростков (повышенный метаболизм)
        if (adjustForAge && ageValue < 18 && ageValue > 12) {
          const youthFactor = Math.min(0.15, (18 - ageValue) * 0.03); // до 15% увеличения
          tdee += Math.round(tdee * youthFactor);
        }
        
        // --- Применение целевого фактора (похудение/поддержание/набор) ---
        const targetCalories = Math.round(tdee * goalFactor);
        
        // --- Расчет макронутриентов ---
        // Будет реализован в функции generateNutritionPlan
        
        // --- Формирование результатов ---
        const calculatedResults = {
          bmr: Math.round(bmr),
          tdee: Math.round(tdee),
          targetCalories: targetCalories,
          goal: goal,
          goalFactor: goalFactor,
          activityLevel: activityLevel,
          activityFactor: activityFactor,
          formula: formula,
          gender: gender,
          age: ageValue,
          weight: {
            value: parseFloatOrDefault(weight),
            unit: measurementSystem === 'metric' ? 'кг' : 'фунт'
          },
          height: {
            value: parseFloatOrDefault(height),
            unit: measurementSystem === 'metric' ? 'см' : 'дюйм'
          },
          bodyFat: bodyFatValue,
          // Дополнительная информация
          date: new Date().toISOString(),
          weeklyChange: goalFactor !== 1.0 ? Math.abs(tdee - targetCalories) * 7 / 7700 : 0, // примерное изменение веса в кг в неделю
        };
        
        setResults(calculatedResults);
        
        // Генерация плана питания на основе рассчитанного TDEE
        generateNutritionPlan(calculatedResults);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, activityLevels, goals, generateNutritionPlan]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      gender: 'male',
      age: '30',
      weight: '75',
      height: '175',
      activityLevel: 'moderate',
      goal: 'maintain',
      formula: 'mifflin',
      measurementSystem: 'metric',
      bodyFat: '',
      includeBodyFat: false,
      includeAdvancedOptions: false,
      dailySteps: '7500',
      workoutDays: '3',
      workoutIntensity: 'moderate',
      stress: 'low',
      sleepHours: '7',
      adjustForAge: true,
      specificDiet: 'none',
      mealFrequency: '3',
      weightChange: '0.5',
      dietHistory: 'none'
    });
    setResults(null);
    setErrors({});
    setSaveProfileName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения профиля
  const handleSaveProfile = () => {
    if (showSaveDialog) {
      const savedResult = saveProfile(saveProfileName || null);
      if (savedResult) {
        alert(`Профиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProfileName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTDEE();
  };
  
  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-emerald-500" />
          Параметры расчета TDEE
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Цель и формула расчета */}
        <GoalAndFormulaSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          goals={goals}
          formulas={formulas}
          activityLevels={activityLevels}
        />

        {/* Дополнительные опции */}
        <AdvancedOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Предпочтения по питанию */}
        <DietPreferencesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения профиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для профиля"
                value={saveProfileName}
                onChange={(e) => setSaveProfileName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProfile}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать TDEE'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProfile}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить профиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;